import { t } from '../i18n.js';

/**
 * Status altijd als icoon én woord, nooit kleur alleen (zoals in het prototype). De vormen
 * verschillen ook: cirkel, ruit, driehoek, achthoek.
 */
const ICOON = {
  good: <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="M4.5 8.2l2.3 2.3 4.7-4.8" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  warning: <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="1.5" width="13" height="13" rx="2.5" transform="rotate(45 8 8)" fill="currentColor" /><rect x="7.1" y="4.2" width="1.8" height="5" rx=".9" fill="#1b212a" /><circle cx="8" cy="11.4" r="1" fill="#1b212a" /></svg>,
  serious: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.2L15.2 14H.8z" fill="currentColor" /><rect x="7.1" y="5.4" width="1.8" height="4.6" rx=".9" fill="#fff" /><circle cx="8" cy="11.8" r="1" fill="#fff" /></svg>,
  critical: <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 1h6l4 4v6l-4 4H5l-4-4V5z" fill="currentColor" /><path d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>,
  neutral: <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="2" /></svg>,
};

export const St = ({ soort, children, icoon }) => <span className={`st ${soort}`}>{icoon ?? ICOON[soort]}{children}</span>;

/** Een draaiend rondje: iets is nog bezig (een lopende uitrol). Zonder beweging bij reduced motion. */
export const Spinner = () => (
  <svg className="spin" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity=".3" />
    <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Stil na vijf minuten zonder bericht (besluit bronnen). Een nieuwe bron wacht en is niet stil. */
export const STIL_NA = 5 * 60 * 1000;
export function bronstatus(bron, nu = Date.now()) {
  if (bron.removed) return { soort: 'neutral', label: t('Verwijderd'), klasse: '' };
  if (bron.status === 'wacht') return { soort: 'neutral', label: t('Wacht op eerste bericht'), klasse: 'pending' };
  if (!bron.lastMessageAt || nu - new Date(bron.lastMessageAt).getTime() > STIL_NA) {
    return { soort: 'critical', label: t('Stil'), klasse: 'silent' };
  }
  return { soort: 'good', label: t('Stuurt'), klasse: '' };
}

/**
 * Kwetsbare versie (besluit kwetsbare-versies): een CVE op CISA KEV, een advisory met ernst critical of
 * high, of een onbekende versie. Niets als er niets is. `onbekend: false` laat het laatste weg (het
 * SOC-scherm).
 */
export function kwetsbaarheid(bron, { onbekend = true } = {}) {
  /* Een ESA en ISE vergelijkt ZERO (nog) niet met Cisco: geen "versie onbekend". */
  if (bron.removed || bron.kind === 'esa' || bron.kind === 'ise') return null;
  if (bron.vuln?.kev) return { soort: bron.kind === 'fmc' ? 'critical' : 'serious', label: t('Actief misbruikt') };
  if (bron.vuln?.high) return { soort: 'warning', label: t('Kwetsbare versie') };
  if (onbekend && !bron.version) return { soort: 'neutral', label: t('Versie onbekend') };
  return null;
}
/**
 * Logt de bron alles wat ZERO nodig heeft (besluit intern-tunnel-logging)? Een firewall die al een dag
 * stuurt maar in 7 dagen geen enkel IPS-event: dan staan de intrusion-events niet naar syslog, en ziet
 * ZERO geen IPS of malware.
 */
const DAG = 24 * 3_600_000;
export function logging(bron, nu = Date.now()) {
  if (bron.removed || bron.kind === 'fmc' || bron.kind === 'esa' || bron.kind === 'ise' || bron.status !== 'actief' || !bron.firstMessageAt) return null;
  if (nu - new Date(bron.firstMessageAt).getTime() < DAG) return null;
  if (bron.lastIpsAt && nu - new Date(bron.lastIpsAt).getTime() < 7 * DAG) return null;
  return { soort: 'warning', label: t('Logt onvolledig') };
}

/* De ernst van Cisco (SIR) in de vier van ZERO. */
export const sirSoort = { Critical: 'critical', High: 'serious', Medium: 'warning', Low: 'neutral', Informational: 'neutral' };

export const SOORT = { 'ftd-fmc': 'FTD · FMC', 'ftd-fdm': 'FTD · FDM', fmc: 'FMC', esa: 'ESA', ise: 'ISE' };

/** Wat een bericht is, op het scherm. De sleutels komen uit src/modules/ingest/ftd.js. */
export const CATEGORIE = {
  weigering: 'Geweigerd verkeer',
  verbinding: 'Verbinding',
  'vpn-mislukt': 'VPN-login mislukt',
  'vpn-einde': 'VPN-sessie beëindigd',
  'vpn-gelukt': 'VPN-login gelukt',
  'beheer-mislukt': 'Beheerlogin mislukt',
  'beheer-gelukt': 'Beheerlogin gelukt',
  configuratie: 'Configuratie gewijzigd',
  deploy: 'Deploy naar firewall',
  'aaa-server': 'Authenticatieserver',
  'vpn-tunnel': 'VPN-tunnel (IKEv2)',
  ips: 'IPS of malware',
  scan: 'Scan (threat detection)',
  herstart: 'Herstart',
  failover: 'Failover',
  interface: 'Interface op of neer',
  statistiek: 'Statistiek van de firewall',
  'fmc-activiteit': 'Activiteit in de FMC',
  systeem: 'Systeemlog van het apparaat',
  overig: 'Overig',
  'zonder-id': 'Zonder bericht-ID',
};

/** Ernst: dezelfde vier als in het prototype, elk met een eigen vorm. */
export const ERNST = { critical: 'Kritiek', serious: 'Hoog', warning: 'Midden', neutral: 'Laag' };
export const Ernst = ({ ernst }) => <St soort={ernst}>{t(ERNST[ernst] ?? ernst)}</St>;
/** Statussen van een alert en van een melding: de waarde is de Nederlandse tekst, dus de sleutel. */
export const STATUS = { nieuw: 'nieuw', behandeling: 'in behandeling', afgesloten: 'afgesloten', hersteld: 'hersteld',
  wacht: 'wacht', verstuurd: 'verstuurd', mislukt: 'mislukt', uit: 'uit', samengevoegd: 'samengevoegd' };

/** De ernst van een Nessus-bevinding (docs/besluiten/nessus-koppeling.md), met dezelfde kleuren als een alert.
    Kleine letters: de controle op vertalingen leest de labels hier: t('Kritiek'), t('Hoog'), t('Midden'), t('Laag'), t('Info'). */
export const nessusErnst = {
  critical: { soort: 'critical', label: 'Kritiek' },
  high: { soort: 'serious', label: 'Hoog' },
  medium: { soort: 'warning', label: 'Midden' },
  low: { soort: 'neutral', label: 'Laag' },
  info: { soort: 'neutral', label: 'Info' },
};
export const NessusErnst = ({ e }) => <St soort={nessusErnst[e]?.soort ?? 'neutral'}>{t(nessusErnst[e]?.label ?? e)}</St>;
